import { and, eq } from "drizzle-orm";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import cuid from "cuid";

import type { DeviceRegistration } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("warmup");

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { client } from "@/config";
import { db } from "@/db/db";
import { permission, workspaceMembership } from "@/db/schema";
import type { Permission, WorkspaceMembership } from "@/db/types";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import UseStorage from "@/hooks/useStorage";
import { subscribeToAppSync } from "@/models/AppsyncHandler.ts";
import useBootstrap from "@/models/BootstrapApplication";
import { bootstrappedApiCalls, fetchLatestBootstrapEventDate, insertBootstrapEvent } from "@/models/BootstrapUtils.ts";
import { setPreferredLanguage as setPreferredLanguageRepo } from "@/models/repositories/Account";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertBroadcastAction } from "@/models/repositories/BroadcastAction";
import { bulkUpsertBroadcastRecipients } from "@/models/repositories/BroadcastRecipient";
import { bulkUpsertDeviceRegistration } from "@/models/repositories/DeviceRegistration";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { bulkUpsertFile } from "@/models/repositories/File";
import { bulkUpsertItemAndCalculateMembership, resetAllItemsLoadedContent } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertScheduleTrigger } from "@/models/repositories/ScheduleTrigger";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { bulkUpsertWorkflowItem } from "@/models/repositories/WorkflowItem";
import { bulkUpsertWsMembershipAvailability } from "@/models/repositories/WsMembershipAvailability";
import { UserContext } from "@/models/StateProviders/userProvider";
import { startTagPolling, stopTagPolling } from "@/models/tagPoller";
import { buildVersion, type MappedWorkspaceRole } from "@/utils";
import { AppContext } from "./AppStateProvider";
import { warmupFeedItems } from "./actions/feedWarmup";
import { downloadPaginatedFeedPermissions } from "./actions/initialFeedLoad";
import { LANGUAGE_LIST, type PreferredLanguage } from "./languages";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";
import { TelemetryContext } from "./TelemetryProvider";

declare const window: Window & { dataLayer: Record<string, unknown>[] };

const LANGUAGE_SESSION_KEY = "preferredLanguage";
const LANGUAGE_DEFAULT_VALUE = LANGUAGE_LIST[0] ?? "none";

type DataState = {
  invalidWorkspace?: boolean;
  setInvalidWorkspace?: (value: boolean) => void;
  availableWorkspaceRoles?: Map<string, MappedWorkspaceRole>;
  bootstrapComplete?: boolean;
  firstBootWorkspaceId?: string;
  loadWorkspaceWorkflowItems?: (params: { workspaceId: string; myAccountId: string }) => Promise<void>;
  preferredLanguage: PreferredLanguage;
  setPreferredLanguage?: (language: PreferredLanguage) => void;
  fetchWorkspaceMembership?: (workspaceMembershipId: string, feedId: string) => Promise<WorkspaceMembership>;
  listFeedPermissions?: (workspaceMembershipId: string, feedId: string) => Promise<Array<Permission>>;
  fetchWorkspaceMembershipAvailabilities?: (workspaceId: string) => Promise<void>;
  fetchGeoCoords?: (workspaceId: string, membershipIds: string[]) => Promise<{ lat: number; lng: number } | null>;
  fetchFeedPermissions?: (feedId: string) => Promise<void>;
  getWorkspaceRoleLabel?: (role: string) => string;
};

//create a context, with createContext api
export const DataContext = createContext<DataState>({
  preferredLanguage: "en",
  invalidWorkspace: false,
});

type Props = {
  children: React.ReactNode | React.ReactNode[];
};

const DataProvider = ({ children }: Props) => {
  const { user, workspaceMembership } = useContext(UserContext);
  const urlParams = useParams();
  const { pubSub } = React.useContext(AppContext);
  const { limitedMemberRole, bootstrapWarmupFeedLimit } = useGlobalFlags();
  const amplitudeSessionTracking = true;
  const { currentFeedId } = useContext(CurrentFeedContext);
  const [invalidWorkspace, setInvalidWorkspace] = useState<boolean>(false);
  const [bootstrapComplete, setBootstrapComplete] = useState<boolean>(false);
  const [firstBootWorkspaceId, setFirstBootWorkspaceId] = useState<string | null>(null);

  const { getLocalStorage, setLocalStorage } = UseStorage();

  const { myAccount } = React.useContext(MyAccountContext);
  const { currentWorkspaceId, workspaces } = React.useContext(WorkspaceContext);
  const telemetryContext = React.useContext(TelemetryContext);
  const deviceKey = `deviceRegistrationId-${currentWorkspaceId}`;
  const [deviceRegistrationId, setDeviceRegistrationId] = useSafeLocalStorage(deviceKey, "");

  const localStoragePreferredLanguage = getLocalStorage("preferredLanguage") as PreferredLanguage;

  const preferredLanguage = localStoragePreferredLanguage || LANGUAGE_DEFAULT_VALUE;

  const setPreferredLanguage = useCallback(
    async (language: PreferredLanguage) => {
      if (!myAccount?.id) return;
      await resetAllItemsLoadedContent();
      await setPreferredLanguageRepo(myAccount.id, language).then(() => {
        setLocalStorage({ key: LANGUAGE_SESSION_KEY, value: language });
      });
    },
    [myAccount?.id, setLocalStorage],
  );

  useEffect(() => {
    //  handsFreeEnabled: handsFreeEnabledForWorkspace(currentWorkspaceId),
    client.appContext.selectedWorkspaceId = currentWorkspaceId;
    client.appContext.workspaces = workspaces?.map((w) => {
      return {
        //  handsFreeEnabled: handsFreeEnabledForWorkspace(w?.id) || false,
        workspaceId: w?.id || "",
        feedSelected: w?.id === currentWorkspaceId ? currentFeedId : undefined,
        languageSelected: preferredLanguage,
      };
    });
  }, [currentWorkspaceId, workspaces, currentFeedId, preferredLanguage]);

  useEffect(() => {
    client.deviceContext.surface = "web";
    client.deviceContext.surfaceContext = window?.navigator?.userAgent;
    client.deviceContext.surfaceBuild = buildVersion();
    client.deviceContext.notificationsEnabled = Notification?.permission === "granted";
    client.deviceContext.internetConnectionStatus = "online";
    client.deviceContext.deviceLanguage = window?.navigator?.language || preferredLanguage;
  }, [deviceRegistrationId, preferredLanguage]);

  // setup mapped workspace roles from api -> client label
  const availableWorkspaceRoles = new Map<string, MappedWorkspaceRole>([
    ["admin", { role: "admin", label: "Administrator", enabled: true }],
    ["member", { role: "member", label: "Organizer", enabled: true }],
    [
      "limitedMember",
      {
        role: "limitedMember",
        label: "Member",
        enabled: limitedMemberRole,
      },
    ],
  ]);

  const getWorkspaceRoleLabel = (role: string) => {
    return availableWorkspaceRoles?.get(role)?.label || role;
  };

  useEffect(() => {
    if (amplitudeSessionTracking) {
      if (amplitudeSessionTracking) {
        const sessionReplayTracking = sessionReplayPlugin({
          sampleRate: 0.3,
        });
        amplitude.add(sessionReplayTracking);
      }
    }
  }, [amplitudeSessionTracking]);

  const { invalidWorkspaceError, bootstrapPaginatedApplication, allComplete } = useBootstrap();
  const warmupFiredRef = React.useRef(false);
  const driverTranscriptsLangRef = React.useRef<string | null>(null);

  useEffect(() => {
    setInvalidWorkspace(() => invalidWorkspaceError);
  }, [invalidWorkspaceError]);

  useEffect(() => {
    const accountIds = user?.session?.credentials?.[0].CredentialScopes?.map((c) => c?.accountId);
    const workspaceId = urlParams?.workspaceId ?? null;

    if (!urlParams?.workspaceId || !workspaceId) {
      return;
    }

    setBootstrapComplete(false);
    setFirstBootWorkspaceId(null);
    warmupFiredRef.current = false;
    driverTranscriptsLangRef.current = null;

    let stale = false;
    if (accountIds?.length > 0 && workspaceId) {
      bootstrapPaginatedApplication({
        bootstrapItems: bootstrappedApiCalls,
        workspaceId: workspaceId ?? null,
        forceBootstrap: true,
      }).then(async () => {
        if (stale) return;
        setFirstBootWorkspaceId(() => workspaceId);
        setBootstrapComplete(() => true);
        subscribeToAppSync({
          accountId: accountIds?.[0],
          currentWorkspaceId: workspaceId,
          pubSub,
          client,
          deviceRegistrationId,
        });
        startTagPolling(workspaceId);
      });
    }

    return () => {
      stale = true;
      stopTagPolling();
    };
  }, [user?.session?.credentials?.[0]?.id, urlParams?.workspaceId]);

  useEffect(() => {
    if (!allComplete || !firstBootWorkspaceId) return;
    if (driverTranscriptsLangRef.current === preferredLanguage) return;

    const isLanguageSwitch = driverTranscriptsLangRef.current !== null;
    driverTranscriptsLangRef.current = preferredLanguage;

    const loadDriverTranscripts = async () => {
      try {
        const registryKey = `driverTranscripts:${preferredLanguage}`;
        const storedDate = isLanguageSwitch ? undefined : await fetchLatestBootstrapEventDate(registryKey);
        const storedTimestamp = storedDate ? new Date(storedDate).toISOString() : undefined;

        const response = await client.bootstrapWorkspaceDriverTranscripts({
          workspaceId: firstBootWorkspaceId,
          language: preferredLanguage,
          format: "Text",
          timestamp: storedTimestamp,
        });

        if (response.items?.length) {
          await bulkUpsertItemAndCalculateMembership(response.items, {});
        }
        if (response.transcriptions?.length) {
          await bulkUpsertWsTranscriptions(response.transcriptions);
        }
        if (response.files?.length) {
          await bulkUpsertFile(response.files);
        }

        await insertBootstrapEvent(registryKey, "transcription");

        log.info("[DRIVER_TRANSCRIPTS] Loaded driver transcripts", {
          items: response.items?.length ?? 0,
          transcriptions: response.transcriptions?.length ?? 0,
          incremental: !!storedTimestamp,
        });
      } catch (err) {
        log.warn("[DRIVER_TRANSCRIPTS] Failed to load driver transcripts", err);
      }
    };

    loadDriverTranscripts();
  }, [allComplete, firstBootWorkspaceId, preferredLanguage]);

  useEffect(() => {
    if (!allComplete || !workspaceMembership || !firstBootWorkspaceId) return;
    if (warmupFiredRef.current) return;
    if (bootstrapWarmupFeedLimit === undefined) return;
    warmupFiredRef.current = true;

    const warmupFeedLimit = typeof bootstrapWarmupFeedLimit === "number" ? bootstrapWarmupFeedLimit : 50;

    log.info("[WARMUP] Initiating feed warmup after bootstrap", {
      workspaceId: firstBootWorkspaceId,
      membershipId: workspaceMembership.id,
      warmupFeedLimit,
    });

    warmupFeedItems({
      workspaceId: firstBootWorkspaceId,
      membership: workspaceMembership,
      trackAction: telemetryContext?.trackAction,
      finishAction: telemetryContext?.finishAction,
      warmupFeedLimit,
    })
      .then((stats) => {
        log.info("[WARMUP] Feed warmup completed successfully", stats);
      })
      .catch((err) => {
        log.warn("[WARMUP] Feed warmup failed but continuing", err);
      });
  }, [allComplete, firstBootWorkspaceId, workspaceMembership?.id, bootstrapWarmupFeedLimit]);

  const loadWorkspaceWorkflowItems = async ({
    workspaceId,
    myAccountId,
  }: {
    workspaceId: string;
    myAccountId: string;
  }) => {
    if (!myAccountId || !workspaceId) return;
    await client.getWorkspaceWorkflowItems(workspaceId).then((resp) => {
      log.debug("Workspace workflow items response", { resp });
      if (resp?.workflowItems?.length) bulkUpsertWorkflowItem(resp.workflowItems);
      // load associated content of the workflow items
      if (resp?.audioEncodings?.length) bulkUpsertWsAudioEncodings(resp.audioEncodings);
      if (resp?.files?.length) bulkUpsertFile(resp.files);
      if (resp?.links?.length) bulkUpsertLink(resp.links);
      if (resp?.transcriptions?.length) bulkUpsertWsTranscriptions(resp.transcriptions);
      if (resp?.displayArtifacts?.length) bulkUpsertDisplayArtifact(resp.displayArtifacts);
    });

    const resp = await client.getScheduledBroadcasts(workspaceId);
    if (resp?.scheduleTriggers?.length > 0) {
      await bulkUpsertScheduleTrigger(resp.scheduleTriggers);
    }
    if (resp?.broadcastActions?.length > 0) {
      await bulkUpsertBroadcastAction(resp.broadcastActions);
    }
    if (resp?.broadcastRecipients?.length > 0) {
      await bulkUpsertBroadcastRecipients(resp.broadcastRecipients);
    }
  };

  const fetchWorkspaceMembership = async (accountId: string, workspaceId: string) => {
    log.info("fetchWorkspaceMembership", accountId, workspaceId);
    const workspaceMembershipRecord = await db.query.workspaceMembership
      .findFirst({
        where: (wm, { eq, and }) =>
          and(eq(wm.accountId, accountId), eq(wm.workspaceId, workspaceId), eq(wm.status, "active")),
      })
      .execute();
    if (!workspaceMembershipRecord) {
      return Promise.reject("Invalid Workspace");
    }
    return workspaceMembershipRecord;
  };

  const listFeedPermissions = React.useCallback(
    async (workspaceMembershipId: string, feedId: string): Promise<Array<Permission>> => {
      log.info("listFeedPermissions", workspaceMembershipId, feedId);
      return await db.query.permission
        .findMany({
          where: and(eq(permission.feedId, feedId), eq(permission.workspaceMembershipId, workspaceMembershipId)),
        })
        .execute();
    },
    [],
  );

  const fetchWorkspaceMembershipAvailabilities = async (workspaceId: string): Promise<void> => {
    // Walk every page. Workspaces like Trimac have thousands of memberships and a single first-page fetch
    // leaves anyone past the page boundary stuck on whatever availability they had at the last status flip,
    // crossing the 10-minute heartbeat threshold and rendering as offline forever.
    const pageSize = 1000;
    // Circuit breaker: 50 pages × 1000/page = 50k memberships covers any plausible workspace and stops
    // a runaway loop if the server ever returns full pages indefinitely.
    const maxPages = 50;

    const fetchPage = async (page: number): Promise<void> => {
      const response = await client.bootstrapWorkspaceMembershipAvailabilities({
        workspaceId,
        page,
        pageSize,
      });

      const records = response?.membershipAvailabilities ?? [];
      if (records.length > 0) {
        await bulkUpsertWsMembershipAvailability(records);
      }

      if (records.length < pageSize) return;
      if (page + 1 >= maxPages) {
        log.warn("[fetchWorkspaceMembershipAvailabilities] Hit max page limit; stopping pagination", {
          workspaceId,
          maxPages,
          pageSize,
        });
        return;
      }
      await fetchPage(page + 1);
    };

    await fetchPage(0);
  };

  const fetchGeoCoords = async (
    workspaceId: string,
    membershipIds: string[],
  ): Promise<{ lat: number; lng: number } | null> => {
    const { data } = await client.getLocations(workspaceId, { membershipIds });
    return data[0]?.locations[0]?.latitude
      ? {
          lat: data[0]?.locations[0]?.latitude,
          lng: data[0]?.locations[0]?.longitude,
        }
      : null;
  };

  const fetchFeedPermissions = async (feedId: string) => {
    await downloadPaginatedFeedPermissions({
      client,
      feedId,
      workspaceId: currentWorkspaceId,
    });
    return;
  };

  useEffect(() => {
    const f = async () => {
      try {
        let deviceId = deviceRegistrationId;
        if (!deviceId) {
          deviceId = cuid();
        }

        // prevent double quotes from entering the DB
        const cleanDeviceId = deviceId.replace(/"/g, "");

        const newDeviceReg = {
          id: cleanDeviceId,
          surface: "web",
        } as DeviceRegistration;
        const payload = {
          deviceRegistration: newDeviceReg,
        };
        // @ts-ignore
        const deviceRegistration = await client.registerDevice(payload);
        if (deviceRegistration?.id) {
          setDeviceRegistrationId(deviceRegistration.id);
          await bulkUpsertDeviceRegistration([deviceRegistration]);
        }
      } catch (error) {
        log.error("Error registering device", error);
      }
    };
    f();
  }, []);

  const dataState: DataState = {
    availableWorkspaceRoles,
    getWorkspaceRoleLabel,
    bootstrapComplete,
    firstBootWorkspaceId,
    preferredLanguage,
    setPreferredLanguage: setPreferredLanguage,
    fetchWorkspaceMembership,
    listFeedPermissions,
    fetchWorkspaceMembershipAvailabilities,
    fetchGeoCoords,
    loadWorkspaceWorkflowItems,
    invalidWorkspace,
    setInvalidWorkspace,
    fetchFeedPermissions,
  };
  return <DataContext.Provider value={dataState}>{children}</DataContext.Provider>;
};
export default DataProvider;
